package com.ijoin.ui.screens

import androidx.compose.foundation.background
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material3.CenterAlignedTopAppBar
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Divider
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.FilterChip
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.RadioButton
import androidx.compose.material3.RadioButtonDefaults
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBarDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.ijoin.data.api.ApiClient
import com.ijoin.data.model.BillingOverview
import com.ijoin.data.model.ConnectAccountStatus
import com.ijoin.data.model.PayoutMethod
import com.ijoin.data.model.PayoutRequest
import com.ijoin.data.model.PayoutStatus
import com.ijoin.data.model.SoldTicketSummary
import com.ijoin.data.repository.BillingFilter
import com.ijoin.i18n.Strings
import com.ijoin.i18n.strings
import com.ijoin.ui.billing.BillingViewModel
import com.ijoin.ui.billing.BillingMessage
import com.ijoin.ui.components.ConnectStatusCard
import com.ijoin.ui.connect.ConnectMessage
import com.ijoin.ui.connect.ConnectViewModel
import com.ijoin.ui.components.NavigationScaffold
import com.ijoin.ui.design.EventCardAccent
import com.ijoin.ui.design.EventCardAccentDark
import com.ijoin.ui.design.EventCardAccentSoft
import com.ijoin.ui.design.EventCardAlert
import com.ijoin.ui.design.EventCardBackground
import com.ijoin.ui.design.EventCardCompactCorner
import com.ijoin.ui.design.EventCardInputCorner
import com.ijoin.ui.design.EventCardOutline
import com.ijoin.ui.design.EventCardWarm
import com.ijoin.ui.design.EventCardWarmSoft
import com.ijoin.ui.design.EventDialog
import com.ijoin.ui.design.EventPill
import com.ijoin.ui.design.EventPrimaryButton
import com.ijoin.ui.design.EventSectionCard
import com.ijoin.ui.design.EventSelectableCard
import com.ijoin.ui.design.EventSegmentButton
import com.ijoin.ui.design.EventSurfaceCard
import com.ijoin.ui.design.EventTextAction
import com.ijoin.ui.design.eventBackgroundBrush
import com.ijoin.ui.design.eventFilterChipBorder
import com.ijoin.ui.design.eventFilterChipColors
import com.ijoin.ui.design.eventOutlinedFieldColors
import com.ijoin.platform.openUrlInApp
import com.ijoin.ui.util.format

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun BillingScreen(
    userId: String,
    onBackClick: () -> Unit,
    onOpenHome: () -> Unit,
    onOpenExplore: () -> Unit,
    onCreateEventClick: () -> Unit,
    onOpenTickets: () -> Unit,
    onOpenProfile: () -> Unit,
    onOpenUrl: (String) -> Unit = {},
    modifier: Modifier = Modifier,
    viewModel: BillingViewModel = remember(userId) { BillingViewModel(userId) },
    connectViewModel: ConnectViewModel = remember { ConnectViewModel() }
) {
    val strings = strings()
    val uiState by viewModel.uiState.collectAsState()
    val connectUiState by connectViewModel.uiState.collectAsState()
    val snackbarHostState = remember { SnackbarHostState() }
    var showWithdrawDialog by remember { mutableStateOf(false) }
    val onTabSelected: (String) -> Unit = { tab ->
        when (tab) {
            "home" -> onOpenHome()
            "explore" -> onOpenExplore()
            "create" -> onCreateEventClick()
            "tickets" -> onOpenTickets()
            "profile" -> onOpenProfile()
        }
    }

    LaunchedEffect(uiState.error) {
        val error = uiState.error ?: return@LaunchedEffect
        snackbarHostState.showSnackbar(error)
    }

    LaunchedEffect(uiState.message) {
        val message = uiState.message ?: return@LaunchedEffect
        val content = when (message) {
            BillingMessage.WithdrawSuccess -> strings.billingWithdrawSuccess
            BillingMessage.WithdrawFailed -> strings.billingWithdrawFailed
            BillingMessage.WithdrawUnavailable -> strings.billingWithdrawUnavailable
        }
        snackbarHostState.showSnackbar(content)
        viewModel.clearMessage()
    }

    LaunchedEffect(connectUiState.message) {
        val message = connectUiState.message ?: return@LaunchedEffect
        val content = when (message) {
            ConnectMessage.DirectPaymentsEnabled -> strings.connectDirectPaymentsEnabled
            ConnectMessage.SwitchedToPlatform -> strings.connectSwitchedToPlatform
        }
        snackbarHostState.showSnackbar(content)
        connectViewModel.clearMessage()
    }

    // Handle onboarding URL - open in-app browser (SFSafariViewController on iOS)
    LaunchedEffect(connectUiState.onboardingUrl) {
        val url = connectUiState.onboardingUrl ?: return@LaunchedEffect
        openUrlInApp(url)
        connectViewModel.clearOnboardingUrl()
    }

    // Handle dashboard URL - open in-app browser (SFSafariViewController on iOS)
    LaunchedEffect(connectUiState.dashboardUrl) {
        val url = connectUiState.dashboardUrl ?: return@LaunchedEffect
        openUrlInApp(url)
        connectViewModel.clearDashboardUrl()
    }

    NavigationScaffold(
        selectedTab = "profile",
        onTabSelected = onTabSelected,
        modifier = modifier,
        containerColor = EventCardBackground,
        snackbarHost = { SnackbarHost(hostState = snackbarHostState) },
        topBar = {
            CenterAlignedTopAppBar(
                title = { Text(strings.billingTitle) },
                navigationIcon = {
                    IconButton(onClick = onBackClick) {
                        Icon(Icons.Default.ArrowBack, contentDescription = null)
                    }
                },
                colors = TopAppBarDefaults.centerAlignedTopAppBarColors(
                    containerColor = EventCardBackground,
                    titleContentColor = EventCardAccentDark,
                    navigationIconContentColor = EventCardAccentDark
                )
            )
        }
    ) { paddingValues ->
        if (uiState.isLoading && uiState.overview == null) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(paddingValues),
                contentAlignment = Alignment.Center
            ) {
                CircularProgressIndicator()
            }
            return@NavigationScaffold
        }

        uiState.overview?.let { overview ->
            BillingContent(
                overview = overview,
                selectedFilter = uiState.selectedFilter,
                onFilterSelected = viewModel::selectFilter,
                onWithdrawClick = { showWithdrawDialog = true },
                isWithdrawLoading = uiState.isCreatingPayout,
                connectStatus = connectUiState.status,
                isConnectLoading = connectUiState.isLoading,
                isSwitchingMode = connectUiState.isSwitchingMode,
                onStartOnboarding = {
                    val baseUrl = "${ApiClient.getBaseUrl()}/billing"
                    connectViewModel.startOnboarding(baseUrl, baseUrl)
                },
                onEnableDirectPayments = connectViewModel::enableDirectPayments,
                onOpenDashboard = connectViewModel::openDashboard,
                onSwitchToPlatform = connectViewModel::switchToPlatform,
                strings = strings,
                modifier = Modifier
                    .fillMaxSize()
                    .background(eventBackgroundBrush())
                    .padding(paddingValues)
            )
        }
    }

    if (showWithdrawDialog) {
        val amountText = uiState.overview?.summary?.currentBalance?.format().orEmpty()
        WithdrawDialog(
            strings = strings,
            amountLabel = amountText,
            isSubmitting = uiState.isCreatingPayout,
            onDismiss = { showWithdrawDialog = false },
            onConfirm = { method, details, note ->
                showWithdrawDialog = false
                viewModel.requestWithdrawal(method, details, note)
            }
        )
    }
}

@Composable
private fun BillingContent(
    overview: BillingOverview,
    selectedFilter: BillingFilter,
    onFilterSelected: (BillingFilter) -> Unit,
    onWithdrawClick: () -> Unit,
    isWithdrawLoading: Boolean,
    connectStatus: ConnectAccountStatus?,
    isConnectLoading: Boolean,
    isSwitchingMode: Boolean,
    onStartOnboarding: () -> Unit,
    onEnableDirectPayments: () -> Unit,
    onOpenDashboard: () -> Unit,
    onSwitchToPlatform: () -> Unit,
    strings: Strings,
    modifier: Modifier = Modifier
) {
    LazyColumn(
        modifier = modifier.padding(horizontal = 16.dp),
        verticalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        item {
            ConnectStatusCard(
                status = connectStatus,
                isLoading = isConnectLoading,
                isSwitchingMode = isSwitchingMode,
                strings = strings,
                onStartOnboarding = onStartOnboarding,
                onEnableDirectPayments = onEnableDirectPayments,
                onOpenDashboard = onOpenDashboard,
                onSwitchToPlatform = onSwitchToPlatform
            )
        }

        item {
            BalanceCard(
                overview = overview,
                onWithdrawClick = onWithdrawClick,
                isWithdrawLoading = isWithdrawLoading,
                strings = strings
            )
        }

        item {
            FilterRow(
                selectedFilter = selectedFilter,
                onFilterSelected = onFilterSelected,
                strings = strings
            )
        }

        item {
            StatsRow(overview = overview, strings = strings)
        }

        item {
            PayoutRequestsSection(
                requests = overview.payoutRequests,
                strings = strings
            )
        }

        overview.requisites?.let { requisites ->
            item {
                RequisitesCard(requisites = requisites, strings = strings)
            }
        }

        item {
            SoldTicketsSection(
                tickets = overview.soldTickets,
                strings = strings
            )
        }

        item { Spacer(modifier = Modifier.height(24.dp)) }
    }
}

@Composable
private fun BalanceCard(
    overview: BillingOverview,
    onWithdrawClick: () -> Unit,
    isWithdrawLoading: Boolean,
    strings: Strings
) {
    val summary = overview.summary
    val canWithdraw = summary.currentBalance.value > 0.0
    EventSurfaceCard(
        modifier = Modifier.fillMaxWidth(),
        backgroundColor = EventCardAccentSoft,
        borderColor = EventCardOutline,
        contentPadding = PaddingValues(20.dp)
    ) {
        Text(
            text = strings.currentBalanceLabel,
            style = MaterialTheme.typography.bodyMedium,
            color = EventCardAccentDark.copy(alpha = 0.7f)
        )
        Text(
            text = summary.currentBalance.format(),
            style = MaterialTheme.typography.headlineMedium,
            fontWeight = FontWeight.Bold,
            color = EventCardAccentDark
        )
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween
        ) {
            Column {
                Text(
                    text = strings.pendingPayoutsLabel,
                    style = MaterialTheme.typography.labelSmall,
                    color = EventCardAccentDark.copy(alpha = 0.7f)
                )
                Text(
                    text = summary.pendingPayouts.format(),
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Medium,
                    color = EventCardAccentDark
                )
            }
            Column(horizontalAlignment = Alignment.End) {
                Text(
                    text = strings.withdrawnTotalLabel,
                    style = MaterialTheme.typography.labelSmall,
                    color = EventCardAccentDark.copy(alpha = 0.7f)
                )
                Text(
                    text = summary.withdrawnTotal.format(),
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Medium,
                    color = EventCardAccentDark
                )
            }
        }
        EventPrimaryButton(
            text = strings.withdrawFundsAction,
            onClick = onWithdrawClick,
            enabled = canWithdraw,
            isLoading = isWithdrawLoading,
            modifier = Modifier.fillMaxWidth()
        )
    }
}

@Composable
private fun WithdrawDialog(
    strings: Strings,
    amountLabel: String,
    isSubmitting: Boolean,
    onDismiss: () -> Unit,
    onConfirm: (PayoutMethod, Map<String, String>, String?) -> Unit
) {
    var method by remember { mutableStateOf(PayoutMethod.ETRANSFER_CANADA) }
    var email by remember { mutableStateOf("") }
    var wiseName by remember { mutableStateOf("") }
    var wiseAccount by remember { mutableStateOf("") }
    var wiseBank by remember { mutableStateOf("") }
    var wiseRouting by remember { mutableStateOf("") }
    var wiseAccountType by remember { mutableStateOf("checking") }
    var note by remember { mutableStateOf("") }

    val rawDetails = when (method) {
        PayoutMethod.ETRANSFER_CANADA -> mapOf("email" to email)
        PayoutMethod.WISE -> mapOf(
            "fullName" to wiseName.trim(),
            "accountNumber" to wiseAccount.trim(),
            "routingNumber" to wiseRouting.trim(),
            "accountType" to wiseAccountType,
            "bankName" to wiseBank.trim()
        )
    }

    val details = rawDetails.filterValues { it.isNotBlank() }

    val isValid = when (method) {
        PayoutMethod.ETRANSFER_CANADA -> email.isNotBlank()
        PayoutMethod.WISE -> wiseName.isNotBlank() && wiseAccount.isNotBlank() && wiseRouting.isNotBlank()
    }

    val focusManager = LocalFocusManager.current
    EventDialog(
        onDismissRequest = onDismiss,
        modifier = Modifier.pointerInput(Unit) {
            detectTapGestures(onTap = { focusManager.clearFocus() })
        }
    ) {
        Text(
            text = strings.withdrawConfirmTitle,
            style = MaterialTheme.typography.titleMedium,
            color = EventCardAccentDark
        )
        Text(
            text = strings.withdrawConfirmMessage(amountLabel),
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )
        Text(
            text = strings.payoutDetailsTitle,
            fontWeight = FontWeight.SemiBold
        )
        MethodSelector(
            selected = method,
            onSelected = { method = it },
            strings = strings
        )
        when (method) {
            PayoutMethod.ETRANSFER_CANADA -> {
                OutlinedTextField(
                    value = email,
                    onValueChange = { email = it },
                    label = { Text(strings.payoutEmailLabel) },
                    singleLine = true,
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(EventCardInputCorner),
                    colors = eventOutlinedFieldColors()
                )
            }
            PayoutMethod.WISE -> {
                OutlinedTextField(
                    value = wiseName,
                    onValueChange = { wiseName = it },
                    label = { Text(strings.payoutWiseNameLabel) },
                    singleLine = true,
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(EventCardInputCorner),
                    colors = eventOutlinedFieldColors()
                )
                OutlinedTextField(
                    value = wiseAccount,
                    onValueChange = { wiseAccount = it },
                    label = { Text(strings.payoutWiseAccountLabel) },
                    singleLine = true,
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(EventCardInputCorner),
                    colors = eventOutlinedFieldColors()
                )
                OutlinedTextField(
                    value = wiseRouting,
                    onValueChange = { wiseRouting = it },
                    label = { Text(strings.payoutWiseRoutingLabel) },
                    singleLine = true,
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(EventCardInputCorner),
                    colors = eventOutlinedFieldColors()
                )
                Text(strings.payoutWiseAccountTypeLabel, fontWeight = FontWeight.SemiBold)
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    AccountTypeOption(
                        label = strings.payoutAccountTypeChecking,
                        selected = wiseAccountType == "checking",
                        onClick = { wiseAccountType = "checking" },
                        modifier = Modifier.weight(1f)
                    )
                    AccountTypeOption(
                        label = strings.payoutAccountTypeSavings,
                        selected = wiseAccountType == "savings",
                        onClick = { wiseAccountType = "savings" },
                        modifier = Modifier.weight(1f)
                    )
                }
                OutlinedTextField(
                    value = wiseBank,
                    onValueChange = { wiseBank = it },
                    label = { Text(strings.payoutWiseBankLabel) },
                    singleLine = true,
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(EventCardInputCorner),
                    colors = eventOutlinedFieldColors()
                )
            }
        }
        OutlinedTextField(
            value = note,
            onValueChange = { note = it },
            label = { Text(strings.payoutNoteLabel) },
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(EventCardInputCorner),
            colors = eventOutlinedFieldColors()
        )
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.End
        ) {
            EventTextAction(
                text = strings.cancel,
                onClick = onDismiss,
                enabled = !isSubmitting
            )
            Spacer(modifier = Modifier.width(8.dp))
            EventPrimaryButton(
                text = strings.withdrawFundsAction,
                onClick = { onConfirm(method, details, note.trim().ifBlank { null }) },
                enabled = isValid && !isSubmitting,
                isLoading = isSubmitting,
                modifier = Modifier.height(40.dp)
            )
        }
    }
}

@Composable
private fun FilterRow(
    selectedFilter: BillingFilter,
    onFilterSelected: (BillingFilter) -> Unit,
    strings: Strings
) {
    LazyRow(horizontalArrangement = Arrangement.spacedBy(12.dp)) {
        items(BillingFilter.values()) { filter ->
            val isSelected = selectedFilter == filter
            FilterChip(
                selected = isSelected,
                onClick = { onFilterSelected(filter) },
                label = { Text(filterLabel(filter, strings)) },
                colors = eventFilterChipColors(),
                border = eventFilterChipBorder(isSelected)
            )
        }
    }
}

@Composable
private fun StatsRow(
    overview: BillingOverview,
    strings: Strings
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(12.dp)
    ) {
        StatCard(
            title = strings.grossSalesLabel,
            value = overview.stats.grossSales.format(),
            modifier = Modifier.weight(1f)
        )
        StatCard(
            title = strings.feesLabel,
            value = overview.stats.fees.format(),
            modifier = Modifier.weight(1f)
        )
        StatCard(
            title = strings.netRevenueLabel,
            value = overview.stats.netRevenue.format(),
            modifier = Modifier.weight(1f)
        )
    }
}

@Composable
private fun StatCard(
    title: String,
    value: String,
    modifier: Modifier = Modifier
) {
    EventSurfaceCard(
        modifier = modifier,
        cornerRadius = EventCardCompactCorner,
        contentPadding = PaddingValues(16.dp)
    ) {
        Text(
            text = title,
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )
        Text(
            text = value,
            style = MaterialTheme.typography.titleMedium,
            fontWeight = FontWeight.Bold,
            color = EventCardAccentDark
        )
    }
}

@Composable
private fun PayoutRequestsSection(
    requests: List<PayoutRequest>,
    strings: Strings
) {
    EventSectionCard(
        title = strings.payoutRequestsTitle
    ) {
        if (requests.isEmpty()) {
            Text(
                text = strings.payoutRequestsEmpty,
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
        } else {
            requests.forEachIndexed { index, request ->
                PayoutRow(request = request, strings = strings)
                if (index != requests.lastIndex) {
                    Divider(color = EventCardOutline)
                }
            }
        }
    }
}

@Composable
private fun PayoutRow(
    request: PayoutRequest,
    strings: Strings
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically
    ) {
        Column(modifier = Modifier.weight(1f)) {
            Text(
                text = request.amount.format(),
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.SemiBold,
                color = EventCardAccentDark
            )
            Text(
                text = methodLabel(request.method, strings),
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
            Text(
                text = formatIsoDate(request.createdAt, strings),
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
            request.details.forEach { (key, value) ->
                Text(
                    text = "${detailLabel(key, strings)}: ${detailValue(key, value, strings)}",
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
            request.note?.takeIf { it.isNotBlank() }?.let { note ->
                Text(
                    text = note,
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
            if (request.status == PayoutStatus.REJECTED) {
                request.rejectionReason?.takeIf { it.isNotBlank() }?.let { reason ->
                    Text(
                        text = reason,
                        style = MaterialTheme.typography.bodySmall,
                        color = EventCardAlert
                    )
                }
            }
        }
        StatusPill(status = request.status, strings = strings)
    }
}

@Composable
private fun StatusPill(
    status: PayoutStatus,
    strings: Strings
) {
    val (label, background, textColor) = when (status) {
        PayoutStatus.PENDING -> Triple(strings.payoutStatusPending, EventCardWarmSoft, EventCardWarm)
        PayoutStatus.PROCESSING -> Triple(strings.payoutStatusProcessing, EventCardAccentSoft, EventCardAccentDark)
        PayoutStatus.PAID -> Triple(strings.payoutStatusPaid, EventCardAccentSoft, EventCardAccentDark)
        PayoutStatus.REJECTED -> Triple(strings.payoutStatusRejected, EventCardAlert.copy(alpha = 0.12f), EventCardAlert)
    }
    EventPill(
        text = label,
        backgroundColor = background,
        textColor = textColor,
        borderColor = EventCardOutline
    )
}

@Composable
private fun RequisitesCard(
    requisites: com.ijoin.data.model.OrganizerRequisites,
    strings: Strings
) {
    EventSectionCard(title = strings.requisitesTitle) {
        InfoRow(label = strings.requisitesAccountLabel, value = requisites.accountNumber.orEmpty())
        InfoRow(label = strings.requisitesInnLabel, value = requisites.inn.orEmpty())
        requisites.kpp?.takeIf { it.isNotBlank() }?.let {
            InfoRow(label = strings.requisitesKppLabel, value = it)
        }
        requisites.bik?.takeIf { it.isNotBlank() }?.let {
            InfoRow(label = strings.requisitesBikLabel, value = it)
        }
        InfoRow(label = strings.organizerLabel, value = requisites.recipientName.orEmpty())
        InfoRow(label = strings.bankLabel, value = requisites.bankName.orEmpty())
    }
}

@Composable
private fun SoldTicketsSection(
    tickets: List<SoldTicketSummary>,
    strings: Strings
) {
    EventSectionCard(title = strings.soldTicketsTitle) {
        if (tickets.isEmpty()) {
            Text(
                text = strings.soldTicketsEmpty,
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
        } else {
            tickets.forEachIndexed { index, ticket ->
                SoldTicketRow(ticket = ticket, strings = strings)
                if (index != tickets.lastIndex) {
                    Divider(color = EventCardOutline)
                }
            }
        }
    }
}

@Composable
private fun SoldTicketRow(
    ticket: SoldTicketSummary,
    strings: Strings
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.Top
    ) {
        Column(modifier = Modifier.weight(1f)) {
            Text(
                text = ticket.eventName,
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.SemiBold
            )
            Text(
                text = strings.soldTicketQuantity(ticket.quantity, ticket.grossAmount.format()),
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
            Text(
                text = strings.soldTicketBuyerInfo(
                    ticket.buyerName,
                    formatIsoDate(ticket.soldAt, strings)
                ),
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }
        Column(horizontalAlignment = Alignment.End) {
            Text(
                text = ticket.netRevenue.format(),
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold,
                color = EventCardAccentDark
            )
            Text(
                text = strings.soldTicketServiceFee(ticket.serviceFee.format()),
                style = MaterialTheme.typography.bodySmall,
                color = EventCardAlert
            )
        }
    }
}

@Composable
private fun InfoRow(label: String, value: String) {
    Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
        Text(
            text = label,
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )
        Text(
            text = value,
            style = MaterialTheme.typography.bodyMedium,
            fontWeight = FontWeight.Medium,
            color = EventCardAccentDark
        )
    }
}

private fun filterLabel(filter: BillingFilter, strings: Strings): String {
    return when (filter) {
        BillingFilter.THIS_WEEK -> strings.billingFilterWeek
        BillingFilter.THIS_MONTH -> strings.billingFilterMonth
        BillingFilter.NINETY_DAYS -> strings.billingFilterQuarter
    }
}

private fun methodLabel(method: PayoutMethod, strings: Strings): String = when (method) {
    PayoutMethod.ETRANSFER_CANADA -> strings.payoutMethodEtransfer
    PayoutMethod.WISE -> strings.payoutMethodWise
}

private fun detailLabel(key: String, strings: Strings): String = when (key) {
    "email" -> strings.payoutEmailLabel
    "fullName" -> strings.payoutWiseNameLabel
    "accountNumber" -> strings.payoutWiseAccountLabel
    "routingNumber" -> strings.payoutWiseRoutingLabel
    "accountType" -> strings.payoutWiseAccountTypeLabel
    "bankName" -> strings.payoutWiseBankLabel
    else -> key.replaceFirstChar { if (it.isLowerCase()) it.titlecase() else it.toString() }
}

private fun detailValue(key: String, value: String, strings: Strings): String = when (key) {
    "accountType" -> when (value.lowercase()) {
        "checking" -> strings.payoutAccountTypeChecking
        "savings" -> strings.payoutAccountTypeSavings
        else -> value
    }
    else -> value
}

private fun formatIsoDate(isoString: String, strings: Strings): String {
    return try {
        val parts = isoString.split("T")
        val date = parts.getOrNull(0)?.split("-") ?: return isoString
        val time = parts.getOrNull(1)?.take(5).orEmpty()
        val day = date.getOrNull(2)?.toIntOrNull() ?: return isoString
        val month = date.getOrNull(1)?.toIntOrNull() ?: return isoString
        val monthName = strings.shortMonthName(month)
        if (monthName.isBlank()) return isoString
        buildString {
            append(day)
            append(' ')
            append(monthName)
            if (time.isNotEmpty()) {
                append(", ")
                append(time)
            }
        }
    } catch (_: Exception) {
        isoString
    }
}
@Composable
private fun MethodSelector(
    selected: PayoutMethod,
    onSelected: (PayoutMethod) -> Unit,
    strings: Strings
) {
    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
        MethodOption(
            label = strings.payoutMethodEtransfer,
            selected = selected == PayoutMethod.ETRANSFER_CANADA,
            onClick = { onSelected(PayoutMethod.ETRANSFER_CANADA) }
        )
        MethodOption(
            label = strings.payoutMethodWise,
            selected = selected == PayoutMethod.WISE,
            onClick = { onSelected(PayoutMethod.WISE) }
        )
    }
}

@Composable
private fun MethodOption(
    label: String,
    selected: Boolean,
    onClick: () -> Unit
) {
    EventSelectableCard(
        selected = selected,
        onClick = onClick,
        modifier = Modifier.fillMaxWidth(),
        contentPadding = PaddingValues(12.dp)
    ) {
        RadioButton(
            selected = selected,
            onClick = onClick,
            colors = RadioButtonDefaults.colors(
                selectedColor = EventCardAccent
            )
        )
        Text(label, style = MaterialTheme.typography.bodyLarge)
    }
}

@Composable
private fun AccountTypeOption(
    label: String,
    selected: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    EventSegmentButton(
        text = label,
        selected = selected,
        onClick = onClick,
        modifier = modifier
    )
}
